/**
 * SharePublica · v8.5.63 · piel — vista pública sin login, sin nav/sidebar.
 * Piel Estudio aplicada al contenido (tipografía, tokens), pero sin ningún
 * cromo del portal.
 *
 * "Si el token permite comentar, un solo campo al final" del spec no se
 * implementa: `permite_comentar` existe como columna en PUBLIC_SHARE_LINKS
 * y se le devuelve al visitante (routes.js), pero no hay ningún endpoint
 * POST público ligado al token que acepte ese comentario — es una columna
 * que nunca se conectó a una ruta de escritura (confirmado contra el
 * backend). Agregar el campo sin esa ruta sería fabricar una acción que no
 * hace nada; se omite por completo hasta que exista ese endpoint.
 *
 * Tampoco hay acciones de decisión (aprobar/pedir cambios): no existe
 * ningún flag `permite_decidir` en el token — un visitante sin sesión nunca
 * puede decidir sobre una pieza compartida, así que DecisionBar no se usa
 * acá bajo ninguna condición.
 */
import { PortalLinkVencido } from "../components/sistema/PaginaPortal.jsx";
import { formatCanal } from "../lib/canales.js";
import { fechaOk } from "../lib/fecha.js";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Lock, ExternalLink, FolderOpen } from "lucide-react";
import { parseNombreTarea } from "../lib/parseNombreTarea.js";

const BASE = import.meta.env.VITE_API_URL || "";
const MESES = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];

function nombreMes(periodo) {
  if (!periodo) return "";
  const [y, m] = periodo.split("-").map(Number);
  if (!y || !MESES[m - 1]) return "";
  return `${MESES[m - 1].charAt(0).toUpperCase() + MESES[m - 1].slice(1)} ${y}`;
}
function formatN(n) {
  const x = parseInt(n, 10);
  if (isNaN(x)) return "0";
  if (x >= 1000000) return (x / 1000000).toFixed(1) + "M";
  if (x >= 1000) return (x / 1000).toFixed(1) + "k";
  return new Intl.NumberFormat("es-PY").format(x);
}
function safeParse(v, fallback) {
  if (!v) return fallback;
  if (typeof v === "object") return v;
  try { return JSON.parse(v); } catch { return fallback; }
}

export default function SharePublica() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = async (pwd) => {
    try {
      // v7.8.1 · password va en header X-Share-Password, nunca en query.
      const url = `${BASE}/api/portal/share/${token}`;
      const headers = pwd ? { "X-Share-Password": pwd } : {};
      const res = await fetch(url, { headers });
      const json = await res.json();
      if (!res.ok) {
        if (json.requiere_password) { setNeedsPassword(true); return; }
        throw new Error(json.error || "Error");
      }
      setData(json);
    } catch (e) {
      setError(e.message);
    }
  };

  useEffect(() => { load(); }, []);

  const submitPwd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    await load(password);
    setSubmitting(false);
  };

  if (error) return <PortalLinkVencido />;

  if (needsPassword) {
    return (
      <Shell>
        <div className="flex items-center gap-2 mb-4">
          <Lock className="h-4 w-4 text-[var(--muted)]"/>
          <h1 className="h1-portal text-[var(--ink)]">Este link tiene contraseña</h1>
        </div>
        <form onSubmit={submitPwd} className="space-y-4">
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} required autoFocus
                 autoComplete="current-password" placeholder="Contraseña"
                 className="w-full px-3 py-2.5 bg-[var(--surface)] border border-[var(--line-control)] rounded-[14px] text-[16px] focus:outline-none focus:border-[var(--accent)] text-[var(--ink)]"/>
          <button type="submit" disabled={submitting}
                  className="w-full bg-[var(--ink)] text-[var(--surface)] py-2.5 rounded-full text-sm font-medium disabled:opacity-40 hover:opacity-90">
            {submitting ? "Verificando…" : "Entrar"}
          </button>
        </form>
      </Shell>
    );
  }

  if (!data) {
    return (
      <Shell>
        <p className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] animate-pulse">Cargando…</p>
      </Shell>
    );
  }

  const cliente = data.cliente;
  const expira = data.expires_at ? new Date(data.expires_at) : null;

  return (
    <div className="min-h-screen bg-[var(--paper)]">
      <header className="bg-[var(--surface)] border-b border-[var(--line)] sticky top-0">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          {cliente?.logo ? (
            <img src={cliente.logo} alt={cliente.nombre} className="w-8 h-8 rounded-[14px] object-contain"/>
          ) : (
            <div className="w-8 h-8 rounded-[14px] text-white text-[13px] font-semibold flex items-center justify-center flex-shrink-0"
                 style={{ background: cliente?.color || "#111318" }}>
              {cliente?.nombre?.slice(0, 2).toUpperCase() || "TC"}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)]">Compartido por The Crew Agencia</div>
            <div className="text-[15px] font-semibold text-[var(--ink)] truncate">{cliente?.nombre || "Cliente"}</div>
          </div>
          {expira && (
            <div className="font-mono text-[12px] text-[var(--faint)] whitespace-nowrap">
              expira {expira.toLocaleDateString("es-PY", { day: "2-digit", month: "short" })}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-8 space-y-6">
        {data.entidad === "reporte" ? <ReportePublico reporte={data.reporte}/> : <TareaPublica tarea={data.tarea || {}}/>}

        <div className="text-center text-[13px] text-[var(--faint)] pt-6 pb-4 font-mono uppercase tracking-[.08em]">
          The Crew Agencia
        </div>
      </main>
    </div>
  );
}

function TareaPublica({ tarea }) {
  return (
    <>
      <div>
        <h1 className="h1-portal text-[var(--ink)]">{parseNombreTarea(tarea.nombre).titulo}</h1>
        <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mt-2 flex items-center gap-2 flex-wrap">
          {tarea.tipo_contenido && <span>{tarea.tipo_contenido}</span>}
          {tarea.canal && <><span>·</span><span>{formatCanal(tarea.canal)}</span></>}
          {fechaOk(tarea.fecha_publicacion) && <><span>·</span><span>Publica {new Date(tarea.fecha_publicacion).toLocaleDateString("es-PY", { day: "2-digit", month: "long" })}</span></>}
        </div>
      </div>

      {tarea.materiales?.finales && (
        <a href={tarea.materiales.finales} target="_blank" rel="noopener"
           className="rounded-[20px] p-4 flex items-center gap-3 hover:border-[var(--ink)] transition-colors">
          <FolderOpen className="h-5 w-5 text-[var(--ink-3)]"/>
          <div className="flex-1">
            <div className="text-[15px] font-semibold text-[var(--ink)]">Material en Drive</div>
            <div className="text-[13px] text-[var(--muted)]">Click para abrir</div>
          </div>
          <ExternalLink className="h-4 w-4 text-[var(--faint)]"/>
        </a>
      )}

      {tarea.descripcion && (
        <div className="border-t border-[var(--line)] pt-4">
          <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-2">Caption</div>
          <p className="text-[15px] text-[var(--ink-2)] whitespace-pre-wrap leading-relaxed">{tarea.descripcion}</p>
        </div>
      )}
    </>
  );
}

/* v8.5.17 · Reporte mensual como link vivo — mismas secciones que Reportes.jsx,
   versión reducida para una vista pública sin login. */
function ReportePublico({ reporte }) {
  if (!reporte) return <p className="text-[15px] text-[var(--muted)] text-center py-8">Reporte no disponible.</p>;
  const m   = safeParse(reporte.metricas_json,   {});
  const top = safeParse(reporte.top_piezas_json, []);

  return (
    <>
      <div>
        <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--accent)]">Reporte mensual</div>
        <h1 className="h1-portal text-[var(--ink)] mt-1 capitalize">{nombreMes(reporte.periodo)}</h1>
      </div>

      {reporte.narrativa && (
        <section className="bg-[var(--raised)] rounded-[20px] p-6">
          <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-3">Lo que pasó en el mes</div>
          <p className="font-display text-[20px] leading-snug text-[var(--ink)] whitespace-pre-wrap">{reporte.narrativa}</p>
        </section>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 bg-[var(--raised)] rounded-[20px] overflow-hidden divide-x divide-y sm:divide-y-0 divide-[var(--line)]">
        <Cifra label="Publicadas" value={m.publicadas || 0}/>
        <Cifra label="Impressions" value={formatN(m.impressions_total || 0)}/>
        <Cifra label="Interacciones" value={formatN(m.interacciones_total || 0)}/>
        <Cifra label="Engagement" value={`${((m.engagement_rate_promedio || 0) * 100).toFixed(1)}%`}/>
      </div>

      {top.length > 0 && (
        <section>
          <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-3">Top del mes</div>
          <div className="divide-y divide-[var(--line)] border-t border-b border-[var(--line)]">
            {top.slice(0, 3).map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-3 py-3">
                <span className="font-display text-[24px] text-[var(--faint)] w-8 text-center">{i + 1}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[15px] font-semibold text-[var(--ink)] truncate">{p.nombre}</div>
                  <div className="text-[13px] text-[var(--muted)] mt-0.5 tabular-nums">{formatN(p.views)} vistas · {formatN(p.likes)} me gusta</div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}

function Cifra({ label, value }) {
  return (
    <div className="px-5 py-5">
      <div className="font-mono text-[12px] uppercase tracking-[.08em] text-[var(--muted)] mb-2">{label}</div>
      <div className="font-display text-[26px] leading-none text-[var(--ink)] tabular-nums">{value}</div>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[var(--paper)]">
      <div className="max-w-md w-full">
        <div className="font-mono text-[13px] uppercase tracking-[.08em] text-[var(--ink-3)] text-center mb-8">The Crew Agencia</div>
        <div className="bg-[var(--surface)] rounded-[20px] p-7">{children}</div>
      </div>
    </div>
  );
}
